/**
 * Publish slots: scheduled posts and ads go out on the hour and the half hour.
 * Admeru's scheduled-publish job runs at :00 and :30, and the server rounds any
 * other time up to the next slot (core/backend/publish_slots.py), so the pickers
 * offer only these times. "Post now" does not wait for a slot.
 *
 * The job's slots are :00 and :30 in UTC. Where the timezone's offset isn't a
 * whole half hour (Nepal, +5:45) they fall at :15 and :45 local time, so the
 * slots here follow the timezone: what a picker shows is when the post goes out.
 */
export const SLOT_MINUTES = 30;
const DAY = 24 * 60;

const pad = (n: number) => String(n).padStart(2, '0');
const hhmm = (mins: number) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

/** Minutes the timezone is ahead of UTC at `at` (the browser's when no zone is given). */
export const offsetMinutes = (tz?: string, at: Date = new Date()): number => {
  if (!tz) return -at.getTimezoneOffset();
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).formatToParts(at).map(p => [p.type, p.value]));
    const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute);
    return Math.round((asUtc - Math.floor(at.getTime() / 60000) * 60000) / 60000);
  } catch {
    return -at.getTimezoneOffset();
  }
};

/** Where the slots fall past the hour in that timezone: 0 (:00/:30) or 15 (:15/:45). */
export const slotPhase = (tz?: string, at?: Date): number =>
  ((offsetMinutes(tz, at) % SLOT_MINUTES) + SLOT_MINUTES) % SLOT_MINUTES;

/** The day's slots, "00:00" … "23:30" (or "00:15" … "23:45" for a :15 phase). */
export const timeSlots = (phase = 0): string[] =>
  Array.from({ length: DAY / SLOT_MINUTES }, (_, i) => hhmm(phase + i * SLOT_MINUTES));

/** The browser timezone's slots. */
export const TIME_SLOTS: string[] = timeSlots(slotPhase());

/**
 * "HH:MM" rounded up to a slot, but never past the day's last slot (a time field
 * can't move the day): 23:45 goes out at 23:30 that day. The server applies the
 * same rule (publish_slots.slot_clock). An unreadable time counts as 10:00.
 */
export const toSlot = (value: string | null | undefined, phase: number = slotPhase()): string => {
  const [h, m] = (value || '').split(':').map(Number);
  const mins = Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : 10 * 60;
  const steps = Math.max(0, Math.ceil((mins - phase) / SLOT_MINUTES));
  const last = phase + DAY - SLOT_MINUTES;
  return hhmm(Math.min(phase + steps * SLOT_MINUTES, last));
};
